// SPDX-License-Identifier: AGPL-3.0-or-later

import {AppDiscoveryCard} from '@app/features/discovery/apps/AppDiscoveryCard';
import styles from '@app/features/discovery/apps/AppDiscoveryPage.module.css';
import AppDiscoveryState, {type AppCategory} from '@app/features/discovery/state/AppDiscoveryState';
import {
	CodeIcon,
	GameControllerIcon,
	MagnifyingGlassIcon,
	MusicNotesIcon,
	RobotIcon,
	ShieldCheckIcon,
	SmileyIcon,
	SparkleIcon,
	WrenchIcon,
	XIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

const CATEGORIES: Array<{id: AppCategory; label: string; icon: React.FC<{size?: number; weight?: any}>}> = [
	{id: 'all', label: 'All Categories', icon: SparkleIcon},
	{id: 'moderation', label: 'Moderation & Security', icon: ShieldCheckIcon},
	{id: 'music', label: 'Music & Audio', icon: MusicNotesIcon},
	{id: 'gaming', label: 'Gaming & Stats', icon: GameControllerIcon},
	{id: 'utility', label: 'Utility & Tools', icon: WrenchIcon},
	{id: 'entertainment', label: 'Entertainment & Fun', icon: SmileyIcon},
];

export const AppDiscoveryPage: React.FC = observer(() => {
	const apps = AppDiscoveryState.filteredApps;
	const selectedCategory = AppDiscoveryState.selectedCategory;
	const searchQuery = AppDiscoveryState.searchQuery;

	const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		AppDiscoveryState.setSearchQuery(e.target.value);
	}, []);

	const handleClearSearch = useCallback(() => {
		AppDiscoveryState.setSearchQuery('');
	}, []);

	const handleCategoryClick = useCallback((cat: AppCategory) => {
		AppDiscoveryState.setSelectedCategory(cat);
	}, []);

	return (
		<div className={styles.container} data-flx="discovery.apps.page">
			<div className={styles.hero}>
				<h1 className={styles.heroTitle}>Discover Apps & Bots</h1>
				<p className={styles.heroSubtitle}>
					Supercharge your Nexoria communities with verified automation, music streaming, security safeguards, and interactive tools.
				</p>

				<div className={styles.searchBarWrapper}>
					<MagnifyingGlassIcon size={20} className={styles.searchIcon} />
					<input
						type="text"
						value={searchQuery}
						onChange={handleSearchChange}
						placeholder="Search applications by name, keyword, or tag..."
						className={styles.searchInput}
						data-flx="discovery.apps.search-input"
					/>
					{searchQuery && (
						<button
							type="button"
							className={styles.clearButton}
							onClick={handleClearSearch}
							aria-label="Clear search"
						>
							<XIcon size={16} />
						</button>
					)}
				</div>
			</div>

			<div className={styles.categoryBar} role="tablist">
				{CATEGORIES.map((cat) => {
					const IconComp = cat.icon;
					const isActive = selectedCategory === cat.id;
					return (
						<button
							key={cat.id}
							type="button"
							role="tab"
							aria-selected={isActive}
							className={`${styles.categoryPill} ${isActive ? styles.categoryPillActive : ''}`}
							onClick={() => handleCategoryClick(cat.id)}
							data-flx={`discovery.apps.category.${cat.id}`}
						>
							<IconComp size={16} weight={isActive ? 'fill' : 'regular'} />
							<span>{cat.label}</span>
						</button>
					);
				})}
			</div>

			<div className={styles.sectionHeader}>
				<h2 className={styles.sectionTitle}>
					{selectedCategory === 'all' ? 'All Verified Applications' : CATEGORIES.find((c) => c.id === selectedCategory)?.label}
					<span style={{fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '8px'}}>
						({apps.length})
					</span>
				</h2>
			</div>

			{apps.length === 0 ? (
				<div className={styles.emptyState}>
					<RobotIcon size={56} weight="duotone" color="#5865f2" />
					<h3 className={styles.emptyTitle}>
						{AppDiscoveryState.allApps.length === 0 ? 'No applications listed yet' : 'No applications found'}
					</h3>
					<p style={{maxWidth: '460px', margin: '8px auto 20px auto', lineHeight: 1.5}}>
						{AppDiscoveryState.allApps.length === 0
							? 'The App Directory is open for developer submissions! Build your bot and apply for verification in the Developer Portal to be reviewed and listed here.'
							: 'Try searching for a different keyword or selecting another category.'}
					</p>
					{AppDiscoveryState.allApps.length === 0 && (
						<a
							href="/developers/applications"
							className={styles.devBannerBtn}
							style={{margin: '0 auto'}}
						>
							<CodeIcon size={18} weight="bold" />
							<span>Go to Developer Portal</span>
						</a>
					)}
				</div>
			) : (
				<div className={styles.grid}>
					{apps.map((app) => (
						<AppDiscoveryCard key={app.id} app={app} />
					))}
				</div>
			)}

			<div className={styles.devBanner}>
				<div className={styles.devBannerLeft}>
					<div className={styles.devIconBox}>
						<CodeIcon size={28} weight="bold" />
					</div>
					<div>
						<h3 className={styles.devBannerTitle}>Are you an application developer?</h3>
						<p className={styles.devBannerSubtitle}>
							Build your bot on Nexoria, request official verification, and showcase your application to thousands of communities.
						</p>
					</div>
				</div>

				<a
					href="/developers/applications"
					className={styles.devBannerBtn}
					data-flx="discovery.apps.developer-portal-link"
				>
					<span>Developer Portal</span>
				</a>
			</div>
		</div>
	);
});
